import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { WarehouseMapConstructor, getSelectedFloorId, warehouseMapConstructorActions } from 'features/WarehouseMapConstructor';
import { ZonesList } from 'widgets/ZonesList';
import { Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { sortFloorsByOrder } from 'entities/Floor';
import { useGetWarehouseByIdQuery } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { ReactComponent as BoxIcon } from 'shared/assets/icons/BoxTabIcon.svg';
import { ReactComponent as SizeCodeIcon } from 'shared/assets/icons/SizeCodeTabIcon.svg';
import { ReactComponent as WarehouseIcon } from 'shared/assets/icons/WarehouseIcon.svg';
import { TabSwitcher } from 'shared/ui/TabSwitcher';

export const WarehouseConstructorPage: FC = memo(() => {
  const { warehouseId } = useParams();

  const { t } = useAppTranslation('warehouse-management');

  const dispatch = useAppDispatch();

  const [selectedTabKey, setSelectedTabKey] = useState('Map');

  const { data, refetch } = useGetWarehouseByIdQuery({ warehouseId, params: { floors: true, internalEquipment: true } });

  const selectedFloorId = useAppSelector(getSelectedFloorId);

  const sortedFloors = useMemo(() => (data?.floors ? sortFloorsByOrder(data.floors) : undefined), [data?.floors]);

  const switchTab = useCallback((tabKey: string): void => {
    setSelectedTabKey(tabKey);
  }, []);

  useEffect(() => {
    if (sortedFloors?.length) {
      dispatch(warehouseMapConstructorActions.changeFloor(sortedFloors[0].floorId));
    }
  }, [dispatch, sortedFloors]);

  const tabs = useMemo(
    () => [
      { key: 'Boxes', label: t('Boxes'), icon: <BoxIcon /> },
      { key: 'Map', label: t('Map'), icon: <BoxIcon /> },
      { key: 'Size codes', label: t('Size codes'), icon: <SizeCodeIcon /> },
      { key: 'Zones', label: t('Zones'), icon: <WarehouseIcon /> },
      { key: 'Locks controller', label: t('Locks controller'), icon: <SizeCodeIcon /> },
    ],
    [t],
  );

  const renderTabContent = useCallback(() => {
    switch (selectedTabKey) {
      case 'Boxes':
        return null;
      case 'Map':
        return (
          <>
            {sortedFloors?.map((floor) =>
              floor.floorId === selectedFloorId ? (
                <WarehouseMapConstructor
                  key={floor.floorId}
                  floors={sortedFloors}
                  selectedFloor={floor}
                  warehouseId={warehouseId}
                  shapesConfig={floor.shapesConfig}
                  refetchFloors={refetch}
                />
              ) : null,
            )}
          </>
        );
      case 'Size codes':
        return null;
      case 'Zones':
        return <ZonesList />;
      case 'Locks controller':
        return null;
      default:
        return null;
    }
  }, [refetch, selectedFloorId, selectedTabKey, sortedFloors, warehouseId]);

  return (
    <div className="mt-7">
      <TabSwitcher tabs={tabs} selectedTabKey={selectedTabKey} onChangeTab={switchTab}>
        <div className="mb-7">
          <Trans
            t={t}
            i18nKey="<0>Warehouse management</0> <1>{{name}}</1>"
            components={[
              <span key="0" className="text-3xl font-semibold" />,
              <span key="1" className="text-3xl font-semibold text-accent" />,
            ]}
            values={{ name: data?.name }}
          />
        </div>
        {renderTabContent()}
      </TabSwitcher>
    </div>
  );
});
